/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import cx from 'classnames';
import * as React from 'react';
import { useTheme, StatusIconMap, WithCSSTransition, SvgChevronRight, Icon, } from '../utils/index.js';
/**
 * Container that allows content to be hidden behind a brief title and a caption.
 * @example
 * <ExpandableBlock title='Basic Block'>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Basic Block' caption='basic caption'>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Default Expanded Block' caption='basic caption' isExpanded={true}>Content in block!</ExpandableBlock>
 * <ExpandableBlock title='Positive status' status='positive'>Content</ExpandableBlock>
 * <ExpandableBlock title='Block with icon' endIcon={<SvgPlaceholder />}>Content</ExpandableBlock>
 */
export const ExpandableBlock = (props) => {
    const { caption, children, className, title, onToggle, style, isExpanded = false, endIcon, status, size = 'default', styleType = 'default', disabled = false, ...rest } = props;
    useTheme();
    const icon = endIcon !== null && endIcon !== void 0 ? endIcon : (status && StatusIconMap[status]());
    const [expanded, setExpanded] = React.useState(isExpanded);
    React.useEffect(() => {
        setExpanded(isExpanded);
    }, [isExpanded]);
    const handleToggle = () => {
        if (disabled) {
            return;
        }
        setExpanded(!expanded);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(!expanded);
    };
    const onKeyDown = (event) => {
        if (event.altKey || disabled) {
            return;
        }
        if (event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'Spacebar') {
            handleToggle();
        }
    };
    return (React.createElement("div", { className: cx('iui-expandable-block', {
            'iui-with-caption': !!caption,
            'iui-expanded': expanded,
            'iui-small': size === 'small',
            'iui-borderless': styleType === 'borderless',
        }, className), style: style, ...rest },
        React.createElement("div", { role: 'button', "aria-expanded": expanded, className: 'iui-header', "aria-disabled": disabled, tabIndex: 0, onClick: handleToggle, onKeyDown: onKeyDown },
            React.createElement(SvgChevronRight, { className: 'iui-icon', "aria-hidden": true }),
            React.createElement("span", { className: 'iui-expandable-block-label' },
                React.createElement("div", { className: 'iui-title' }, title),
                caption && React.createElement("div", { className: 'iui-caption' }, caption)),
            icon && React.createElement(Icon, { fill: status }, icon)),
        React.createElement(WithCSSTransition, { in: expanded },
            React.createElement("div", { className: 'iui-expandable-content' },
                React.createElement("div", null, children)))));
};
export default ExpandableBlock;
